import { ReactNode, useState } from "react";
import Button from "../../../student/components/generic/button";
import {
  DMModal,
  DMModalContent,
  DMModalFooter,
  DMModalTitle,
} from "../../../shared/DMModal";
import { useForm } from "react-hook-form";
import { useMutation } from "@tanstack/react-query";
import { executeQuery } from "../../../utils";
import { SearchTagData } from "../../types";
import { compact } from "lodash";

type FormInputs = {
  searchTag: string;
  associations: string;
};

export const EditSearchTagsModal: React.FC<{
  onSave: () => void;
  searchDataToEdit?: SearchTagData;
  children: (onClick: () => void) => ReactNode;
}> = ({ onSave, searchDataToEdit, children }) => {
  const [isVisible, setIsVisible] = useState(false);
  const [nonFieldError, setNonFieldError] = useState<string | null>(null);
  const {
    handleSubmit,
    register,
    reset,
    formState: { errors },
  } = useForm<FormInputs>(
    searchDataToEdit
      ? {
          defaultValues: {
            searchTag: searchDataToEdit.searchTag,
            associations: searchDataToEdit.associations.join(", "),
          },
        }
      : {}
  );

  const { mutate: mutateSearchTag, isPending } = useMutation({
    mutationFn: async ({
      searchTagData,
      type,
    }: {
      searchTagData: SearchTagData & { originalSearchTag?: string };
      type: "all" | "single";
    }) => {
      return executeQuery({
        path: `/manager_new/module/search_tags`,
        method: "post",
        requestBody: {
          searchTagData,
          type,
        },
      });
    },
  });

  const close = () => {
    setIsVisible(false);
    setNonFieldError(null);
  };

  const onSubmit = (data: FormInputs, type: "all" | "single") => {
    setNonFieldError(null);
    mutateSearchTag(
      {
        searchTagData: {
          searchTag: data.searchTag,
          associations: compact(
            data.associations
              .split(",")
              .map((association) => association.trim())
          ),
          ...(searchDataToEdit && {
            originalSearchTag: searchDataToEdit.searchTag,
          }),
        },
        type,
      },
      {
        onSuccess: () => {
          onSave();
          reset();
          close();
        },
        onError: () => {
          setNonFieldError("An error occurred while editing the search tag");
        },
      }
    );
  };

  return (
    <>
      <div>{children(() => setIsVisible(true))}</div>

      <DMModal visible={isVisible} onClose={close}>
        <form
          className="flex flex-col gap-10"
          onSubmit={(e) => e.preventDefault()}
        >
          <DMModalTitle>
            {searchDataToEdit ? "Edit Search Tag" : "Create a New Search Tag"}
          </DMModalTitle>
          <DMModalContent>
            <div className="flex w-full flex-col gap-1">
              <label className="text-sm" htmlFor="searchTag">
                Search Tag
              </label>
              <input
                type="text"
                id="searchTag"
                className="h-10 rounded px-2 py-0"
                {...register("searchTag", {
                  required: "This field is required",
                })}
              />
              {errors.searchTag && (
                <span className="text-sm text-dm-error-500">
                  {errors.searchTag.message}
                </span>
              )}
            </div>

            <div className="flex w-full flex-col gap-1">
              <label className="text-sm" htmlFor="associations">
                Associations
              </label>
              <textarea
                id="associations"
                className="h-16 w-full resize-y rounded p-2"
                {...register("associations")}
              />
            </div>

            {nonFieldError && (
              <p className="w-full rounded bg-dm-error-100 px-4 py-2 text-center text-sm text-dm-error-500">
                {nonFieldError}
              </p>
            )}
          </DMModalContent>
          <DMModalFooter>
            <Button type="outline" onClick={close} submit={false} size="xsmall">
              Cancel
            </Button>
            <div className="flex gap-2">
              {searchDataToEdit && (
                <Button
                  size="xsmall"
                  type="secondary"
                  onClick={handleSubmit((data) => onSubmit(data, "all"))}
                  submit={false}
                  disabled={isPending}
                >
                  Save Edits for All Existing Skills
                </Button>
              )}
              <Button
                size="xsmall"
                onClick={handleSubmit((data) => onSubmit(data, "single"))}
                submit={false}
                disabled={isPending}
              >
                Save
              </Button>
            </div>
          </DMModalFooter>
        </form>
      </DMModal>
    </>
  );
};
